import {
    Vec2Mm,
    Vec3Mm,
    liftFromPlane,
    parameteriseClosed,
    polygonSetSdf2d,
    projectToPlane,
    resampleClosedPolygon,
} from './crossSectionGeometry';
import { throwIfAborted } from '../utils';
import { VariationalField, evaluateField } from './crossSectionFieldSampling';
import { PreparedSection } from './crossSectionPreparation';
import { CrossSectionError } from './crossSectionTypes';

interface ConstraintSet {
    readonly centresMm: Float64Array;
    readonly values: Float64Array;
    readonly count: number;
}

/** Two constraints closer than this, asking for the same value, are one constraint. */
const DUPLICATE_CENTRE_EPSILON_MM = 1e-3;
const POLY_TERMS = 4;
/** Ridge added to the reduced kernel (unit-radius coordinates) when a factorisation fails. */
const RIDGE_LADDER = [1e-10, 1e-8, 1e-6, 1e-4];
/** Below this fraction of the leading pivot, the polynomial block is rank-deficient: a flat cloud. */
const RANK_TOLERANCE = 1e-9;
const CANCELLATION_CHECK_MASK = 0x0f;
const MIN_SAMPLES_PER_CONTOUR = 3;
/** A densification step must cut the worst residual to this fraction to earn another. */
const MIN_STEP_IMPROVEMENT = 0.7;
const MIN_OFFSET_FRACTION = 1 / 16;
const NEIGHBOUR_CELLS = [-1, 0, 1].flatMap((x) =>
    [-1, 0, 1].flatMap((y) => [-1, 0, 1].map((z) => [x, y, z]))
);

function dedupeConstraints(
    centresMm: readonly number[],
    values: readonly number[]
): ConstraintSet {
    const keptCentres: number[] = [];
    const keptValues: number[] = [];
    const eps = DUPLICATE_CENTRE_EPSILON_MM;
    const buckets = new Map<string, number[]>();
    for (let i = 0; i < values.length; i++) {
        const [x, y, z] = centresMm.slice(3 * i, 3 * i + 3);
        const [cx, cy, cz] = [x, y, z].map((v) => Math.floor(v / eps));
        const duplicate = NEIGHBOUR_CELLS.some(([ax, ay, az]) =>
            (buckets.get(`${cx + ax},${cy + ay},${cz + az}`) ?? []).some(
                (j) => {
                    const dx = x - keptCentres[3 * j];
                    const dy = y - keptCentres[3 * j + 1];
                    const dz = z - keptCentres[3 * j + 2];
                    return (
                        dx * dx + dy * dy + dz * dz < eps * eps &&
                        Math.abs(values[i] - keptValues[j]) < eps
                    );
                }
            )
        );
        if (!duplicate) {
            const key = `${cx},${cy},${cz}`;
            buckets.set(key, [...(buckets.get(key) ?? []), keptValues.length]);
            keptCentres.push(x, y, z);
            keptValues.push(values[i]);
        }
    }
    return {
        centresMm: Float64Array.from(keptCentres),
        values: Float64Array.from(keptValues),
        count: keptValues.length,
    };
}

/** Solves the triharmonic interpolation system; @throws CrossSectionError `degenerate-rank` for fewer than four or coplanar centres, the abort error once `signal` fires. */
function solveVariational(
    constraints: ConstraintSet,
    regularisationLambda: number,
    signal?: AbortSignal
): VariationalField {
    const n = constraints.count;
    if (n < POLY_TERMS) {
        throw new CrossSectionError(
            'degenerate-rank',
            `the variational fit needs at least ${POLY_TERMS} constraints, got ${n}`
        );
    }
    const c = constraints.centresMm;
    const axis = (o: number) =>
        Float64Array.from({ length: n }, (_, i) => c[3 * i + o]);
    const [xsMm, ysMm, zsMm] = [axis(0), axis(1), axis(2)];
    const [cx, cy, cz] = [xsMm, ysMm, zsMm].map(
        (a) => a.reduce((sum, v) => sum + v, 0) / n
    );
    let radius = 0;
    for (let i = 0; i < n; i++) {
        const dx = xsMm[i] - cx;
        const dy = ysMm[i] - cy;
        const dz = zsMm[i] - cz;
        radius = Math.max(radius, Math.sqrt(dx * dx + dy * dy + dz * dz));
    }
    if (radius <= DUPLICATE_CENTRE_EPSILON_MM) {
        throw new CrossSectionError(
            'degenerate-rank',
            'the cross-sections do not determine a solid: all constraint points coincide'
        );
    }
    const xs = xsMm.map((x) => (x - cx) / radius);
    const ys = ysMm.map((y) => (y - cy) / radius);
    const zs = zsMm.map((z) => (z - cz) / radius);

    const p = new Float64Array(n * POLY_TERMS);
    for (let i = 0; i < n; i++) {
        p.set([1, xs[i], ys[i], zs[i]], POLY_TERMS * i);
    }
    const reflectors = new Float64Array(POLY_TERMS * n);
    const beta = new Float64Array(POLY_TERMS);
    const r = new Float64Array(POLY_TERMS * POLY_TERMS);
    const reflector = (k: number) => reflectors.subarray(k * n, (k + 1) * n);
    const sum = (from: number, term: (i: number) => number) => {
        let total = 0;
        for (let i = from; i < n; i++) {
            total += term(i);
        }
        return total;
    };
    /** Applies reflector k to the vector x[stride·i + offset], in place. */
    const reflect = (k: number, x: Float64Array, stride = 1, offset = 0) => {
        const v = reflector(k);
        const s = beta[k] * sum(k, (i) => v[i] * x[stride * i + offset]);
        for (let i = k; i < n; i++) {
            x[stride * i + offset] -= s * v[i];
        }
    };
    for (let k = 0; k < POLY_TERMS; k++) {
        const v = reflector(k);
        for (let i = k; i < n; i++) {
            v[i] = p[POLY_TERMS * i + k];
        }
        const norm2 = sum(k, (i) => v[i] * v[i]);
        v[k] -= v[k] >= 0 ? -Math.sqrt(norm2) : Math.sqrt(norm2);
        const vnorm2 = sum(k, (i) => v[i] * v[i]);
        beta[k] = vnorm2 > 0 ? 2 / vnorm2 : 0;
        for (let j = k; j < POLY_TERMS; j++) {
            reflect(k, p, POLY_TERMS, j);
            r[POLY_TERMS * k + j] = p[POLY_TERMS * k + j];
        }
    }
    for (let k = 0; k < POLY_TERMS; k++) {
        if (Math.abs(r[POLY_TERMS * k + k]) < RANK_TOLERANCE * Math.abs(r[0])) {
            throw new CrossSectionError(
                'degenerate-rank',
                'the cross-sections do not determine a solid: every constraint ' +
                    'point lies in one plane'
            );
        }
    }

    throwIfAborted(signal);
    const a = new Float64Array(n * n);
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
            const dx = xs[i] - xs[j];
            const dy = ys[i] - ys[j];
            const dz = zs[i] - zs[j];
            const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
            a[i * n + j] = a[j * n + i] = d * d * d;
        }
        a[i * n + i] = regularisationLambda;
    }
    const w = new Float64Array(n);
    for (let k = 0; k < POLY_TERMS; k++) {
        const v = reflector(k);
        const b = beta[k];
        if (b === 0) {
            continue;
        }
        let t = 0;
        for (let i = 0; i < n; i++) {
            w[i] = sum(k, (j) => a[i * n + j] * v[j]);
            t += v[i] * w[i];
        }
        const bbt = b * b * t;
        for (let i = 0; i < n; i++) {
            for (let j = 0; j < n; j++) {
                a[i * n + j] +=
                    bbt * v[i] * v[j] - b * (v[i] * w[j] + w[i] * v[j]);
            }
        }
    }
    const rhs = Float64Array.from(constraints.values);
    for (let k = 0; k < POLY_TERMS; k++) {
        reflect(k, rhs);
    }

    const m = n - POLY_TERMS;
    const at = (i: number, j: number) => (POLY_TERMS + i) * n + POLY_TERMS + j;
    const diagonal = Float64Array.from({ length: m }, (_, i) => a[at(i, i)]);
    const factorised = [0, ...RIDGE_LADDER].some((ridge) =>
        choleskyReduced(a, n, m, diagonal, ridge, signal)
    );
    if (!factorised) {
        throw new CrossSectionError(
            'degenerate-rank',
            'the cross-sections do not determine a solid: the interpolation ' +
                'system could not be factorised'
        );
    }
    const gamma = new Float64Array(m);
    for (let i = 0; i < m; i++) {
        let s = rhs[POLY_TERMS + i];
        for (let k = 0; k < i; k++) {
            s -= a[at(i, k)] * gamma[k];
        }
        gamma[i] = s / a[at(i, i)];
    }
    for (let i = m - 1; i >= 0; i--) {
        let s = gamma[i];
        for (let k = i + 1; k < m; k++) {
            s -= a[at(k, i)] * gamma[k];
        }
        gamma[i] = s / a[at(i, i)];
    }

    // Rows 0..3 of `a` are untouched by the factorisation, so they still hold M₁₂.
    const polyScaled = new Float64Array(POLY_TERMS);
    for (let k = POLY_TERMS - 1; k >= 0; k--) {
        let s = rhs[k];
        for (let j = 0; j < m; j++) {
            s -= a[k * n + POLY_TERMS + j] * gamma[j];
        }
        for (let j = k + 1; j < POLY_TERMS; j++) {
            s -= r[POLY_TERMS * k + j] * polyScaled[j];
        }
        polyScaled[k] = s / r[POLY_TERMS * k + k];
    }
    const lambda = new Float64Array(n);
    lambda.set(gamma, POLY_TERMS);
    for (let k = POLY_TERMS - 1; k >= 0; k--) {
        reflect(k, lambda);
    }
    const radius3 = radius * radius * radius;
    lambda.forEach((l, i) => {
        lambda[i] = l / radius3;
    });
    const poly = new Float64Array(POLY_TERMS);
    poly[1] = polyScaled[1] / radius;
    poly[2] = polyScaled[2] / radius;
    poly[3] = polyScaled[3] / radius;
    poly[0] = polyScaled[0] - poly[1] * cx - poly[2] * cy - poly[3] * cz;
    return { xsMm, ysMm, zsMm, lambda, poly, centreCount: n };
}

/** In-place Cholesky of the trailing m×m block of `a`, reading only its upper triangle and `diagonal` so it can be retried; false on a non-positive pivot. */
function choleskyReduced(
    a: Float64Array,
    n: number,
    m: number,
    diagonal: Float64Array,
    ridge: number,
    signal: AbortSignal | undefined
): boolean {
    const trace = diagonal.reduce((sum, d) => sum + (d + ridge), 0);
    const tolerance = m > 0 ? (1e-13 * trace) / m : 0;
    const off = POLY_TERMS;
    for (let i = 0; i < m; i++) {
        if ((i & CANCELLATION_CHECK_MASK) === 0) {
            throwIfAborted(signal);
        }
        const rowI = (off + i) * n + off;
        for (let j = 0; j < i; j++) {
            const rowJ = (off + j) * n + off;
            let s = a[rowJ + i];
            for (let k = 0; k < j; k++) {
                s -= a[rowI + k] * a[rowJ + k];
            }
            a[rowI + j] = s / a[rowJ + j];
        }
        let s = diagonal[i] + ridge;
        for (let k = 0; k < i; k++) {
            s -= a[rowI + k] * a[rowI + k];
        }
        if (!(s > tolerance)) {
            return false;
        }
        a[rowI + i] = Math.sqrt(s);
    }
    return true;
}

export interface FitOptions {
    readonly residualToleranceMm: number;
    readonly offSurfaceOffsetMm: number;
    readonly maxCentres: number;
    /** Ridge on the kernel in unit-radius coordinates: 0 interpolates exactly. */
    readonly regularisationLambda: number;
    readonly minSamplesPerContour: number;
    readonly initialSampleSpacingMm: number;
    readonly maxTurnDeg: number;
    readonly signal?: AbortSignal;
}

const DEFAULT_FIT_OPTIONS: FitOptions = {
    residualToleranceMm: 0.25,
    offSurfaceOffsetMm: 0.75,
    maxCentres: 1500,
    regularisationLambda: 1e-3,
    minSamplesPerContour: 32,
    initialSampleSpacingMm: 6,
    maxTurnDeg: 15,
};

export interface FitResult {
    readonly field: VariationalField;
    readonly maxResidualMm: number;
    readonly toleranceMet: boolean;
    readonly centreCount: number;
}

/** Whether `uv` lies strictly inside the union of a plane's `rings`, buried by another contour and so not a boundary. */
function isInteriorToSectionUnion(
    rings: readonly (readonly Vec2Mm[])[],
    uv: Vec2Mm
): boolean {
    return (
        rings.length > 1 &&
        polygonSetSdf2d(rings, uv) < -DUPLICATE_CENTRE_EPSILON_MM
    );
}

/** The largest |field| over each contour's patient-mm vertices, skipping those strictly inside the union of `rings`. */
function worstResiduals(
    field: VariationalField,
    { frame }: PreparedSection,
    rings: readonly (readonly Vec2Mm[])[],
    contoursMm: readonly (readonly Vec3Mm[])[]
): number[] {
    return contoursMm.map((contour) => {
        let worst = 0;
        for (const v of contour) {
            const uv = projectToPlane(frame, v);
            const r = isInteriorToSectionUnion(rings, uv)
                ? 0
                : Math.abs(evaluateField(field, v[0], v[1], v[2]));
            worst = r > worst ? r : worst;
        }
        return worst;
    });
}

/** Appends on-surface (0) and paired off-surface (±δ) constraints for up to `maxSamplesPerContour` samples of each fit ring. */
function appendSectionConstraints(
    section: PreparedSection,
    samplesPerContour: readonly number[],
    maxSamplesPerContour: number,
    { offSurfaceOffsetMm, maxTurnDeg }: FitOptions,
    centresMm: number[],
    values: number[]
): void {
    const rings = section.fitPolygons2d;
    const floor = offSurfaceOffsetMm * MIN_OFFSET_FRACTION;
    const along = (q: Vec2Mm, d: number, normal: Vec2Mm): Vec2Mm => [
        q[0] + d * normal[0],
        q[1] + d * normal[1],
    ];
    const sdf = (q: Vec2Mm, d: number, normal: Vec2Mm) =>
        polygonSetSdf2d(rings, along(q, d, normal));
    const push = (uv: Vec2Mm, value: number) => {
        const p = liftFromPlane(section.frame, uv);
        centresMm.push(p[0], p[1], p[2]);
        values.push(value);
    };
    rings.forEach((fitRing, p) => {
        const ring = resampleClosedPolygon(
            fitRing,
            samplesPerContour[p],
            maxTurnDeg,
            maxSamplesPerContour
        );
        const n = ring.length;
        ring.forEach((q, i) => {
            if (isInteriorToSectionUnion(rings, q)) {
                return;
            }
            const [previous, next] = [ring[(i - 1 + n) % n], ring[(i + 1) % n]];
            const tangent = [next[0] - previous[0], next[1] - previous[1]];
            const tangentLength = Math.sqrt(
                tangent[0] * tangent[0] + tangent[1] * tangent[1]
            );
            if (tangentLength < DUPLICATE_CENTRE_EPSILON_MM) {
                return;
            }
            let normal: Vec2Mm = [
                tangent[1] / tangentLength,
                -tangent[0] / tangentLength,
            ];
            const probe = Math.min(floor, 0.05);
            if (!(sdf(q, probe, normal) > sdf(q, -probe, normal))) {
                normal = [-normal[0], -normal[1]];
            }
            push(q, 0);
            for (let delta = offSurfaceOffsetMm; delta >= floor; delta /= 2) {
                if (sdf(q, delta, normal) > 0 && sdf(q, -delta, normal) < 0) {
                    push(along(q, delta, normal), delta);
                    push(along(q, -delta, normal), -delta);
                    break;
                }
            }
        });
    });
}

interface FitAttempt {
    readonly field: VariationalField;
    readonly residuals: number[][];
    readonly worst: number;
}

/**
 * Fits an implicit surface through the fit rings, densifying rings off tolerance within `maxCentres`; `maxResidualMm` is measured at the drawn vertices.
 * @throws CrossSectionError `degenerate-rank` for a coplanar constraint cloud, the abort error once `signal` fires.
 */
export function fitVariationalField(
    sections: readonly PreparedSection[],
    overrides: Partial<FitOptions> = {}
): FitResult {
    const options: FitOptions = { ...DEFAULT_FIT_OPTIONS, ...overrides };
    const contourCount = sections.reduce(
        (total, s) => total + s.fitPolygons2d.length,
        0
    );
    const affordable = Math.floor(
        options.maxCentres / (3 * Math.max(1, contourCount))
    );
    const maxSamplesPerContour = Math.max(MIN_SAMPLES_PER_CONTOUR, affordable);
    const fitRingsMm = sections.map((s) =>
        s.fitPolygons2d.map((ring) =>
            ring.map((q) => liftFromPlane(s.frame, q))
        )
    );
    const samples = sections.map((s) =>
        s.fitPolygons2d.map((polygon) => {
            const perimeterMm = parameteriseClosed(polygon).perimeterMm;
            const wanted = Math.max(
                options.minSamplesPerContour,
                Math.ceil(perimeterMm / options.initialSampleSpacingMm)
            );
            return Math.max(
                MIN_SAMPLES_PER_CONTOUR,
                Math.min(affordable, wanted)
            );
        })
    );
    let best: FitAttempt | null = null;
    for (let attempt = 0; ; attempt++) {
        throwIfAborted(options.signal);
        const centresMm: number[] = [];
        const values: number[] = [];
        sections.forEach((section, s) =>
            appendSectionConstraints(
                section,
                samples[s],
                maxSamplesPerContour,
                options,
                centresMm,
                values
            )
        );
        const constraints = dedupeConstraints(centresMm, values);
        if (constraints.count > options.maxCentres && best) {
            return finish(sections, best, false);
        }
        const field = solveVariational(
            constraints,
            options.regularisationLambda,
            options.signal
        );
        const residuals = sections.map((section, s) =>
            worstResiduals(field, section, section.fitPolygons2d, fitRingsMm[s])
        );
        const worst = Math.max(...residuals.flat());
        const current: FitAttempt = { field, residuals, worst };
        // Denser sampling of disagreeing contours can make the interpolant swing harder, so the best attempt is kept.
        const previousWorst = best?.worst ?? Infinity;
        if (current.worst < previousWorst) {
            best = current;
        }
        if (current.worst <= options.residualToleranceMm) {
            return finish(sections, current, true);
        }
        if (
            current.worst > MIN_STEP_IMPROVEMENT * previousWorst ||
            constraints.count >= options.maxCentres
        ) {
            return finish(sections, best ?? current, false);
        }
        let densified = false;
        samples.forEach((perSection, s) =>
            perSection.forEach((count, p) => {
                const grown = Math.min(
                    maxSamplesPerContour,
                    Math.ceil(count * 1.5)
                );
                if (
                    residuals[s][p] > options.residualToleranceMm &&
                    grown > count
                ) {
                    perSection[p] = grown;
                    densified = true;
                }
            })
        );
        if (!densified || attempt > 12) {
            return finish(sections, best ?? current, false);
        }
    }
}

/** The fit result for `state`, its residual measured against the contours as drawn. */
function finish(
    sections: readonly PreparedSection[],
    state: FitAttempt,
    toleranceMet: boolean
): FitResult {
    const drawnResiduals = sections.flatMap((s) =>
        worstResiduals(state.field, s, s.polygons2d, s.polygonsMm)
    );
    return {
        field: state.field,
        maxResidualMm: Math.max(0, ...drawnResiduals),
        toleranceMet,
        centreCount: state.field.centreCount,
    };
}
